import Component from '@glimmer/component'
import { tracked } from '@glimmer/tracking'
import { isEmpty } from '@ember/utils'
import { SubscriptionType } from 'teacher-ui-client/subscription-type/subscription-types'
import type Student from 'teacher-ui-client/models/student'
import { DataProcessor } from '@blakeelearning/data-tables/utils/data-processor'
import { on } from '@ember/modifier'
import { fn } from '@ember/helper'
import { t } from 'ember-intl'
import { eq } from 'ember-truth-helpers'
import { formatValue } from 'teacher-ui-client/utils/format-value'
import { formatDuration } from 'teacher-ui-client/utils/format-duration'
import { gradeName } from 'teacher-ui-client/helpers/grade-name'
import { SortColumnIcon } from 'teacher-ui-client/components/sort-column-icon'
import { BasePageSelector } from 'teacher-ui-client/components/base/page-selector/index'
import type Owner from '@ember/owner'
import type { TimeOnTaskHistoryDataItem } from 'teacher-ui-client/utils/student-events/fetchers/time-on-task-history'
import { hasId } from 'teacher-ui-client/utils/type-guards'

interface Args {
  students: Student[]
  subscriptionType: SubscriptionType
  data: TimeOnTaskHistoryDataItem[]
  isSubco?: boolean
}

interface Signature {
  Element: HTMLDivElement
  Args: Args
}

type StudentData = {
  externalId: string
  firstName: string
  gradePosition: number
  id: string
  lastName: string
  schoolClasses: string
  teacherName: string
} & Partial<TimeOnTaskHistoryDataItem>

const headingClass: Partial<Record<SubscriptionType, string>> = {
  [SubscriptionType.Reading]: 'bg-oceany-blue-100 text-black',
  [SubscriptionType.Maths]: 'bg-ms-green-100 text-black',
}
export class TimeOnTaskTable extends Component<Signature> {
  @tracked currentPage = 1

  @tracked perPage = 30

  @tracked sortingPriorityQueue = []

  processor: DataProcessor<StudentData>

  constructor(owner: Owner, args: Args) {
    super(owner, args)
    this.processor = new DataProcessor(this)
  }

  get headingClass() {
    return headingClass[this.args.subscriptionType] ?? ''
  }

  get pageCount() {
    const { data, perPage } = this
    return Math.ceil(data.length / perPage)
  }

  get itemCount() {
    return this.data.length
  }

  get data(): StudentData[] {
    return this.args.students.filter(hasId).map((student) => {
      const studentStats = this.args.data.find((item) => String(item.student_id) === student.id) ?? undefined

      return {
        id: student.id,
        firstName: student.firstName,
        lastName: student.lastName,
        externalId: student.externalId ?? '',
        gradePosition: student.gradePosition,
        ...studentStats,
        teacherName: student
          .teachersFor(this.args.subscriptionType)
          .map((teacher) => teacher.fullName)
          .join(', '),
        schoolClasses: student
          .schoolClassesFor(this.args.subscriptionType)
          .map((schoolClass) => schoolClass.name)
          .join(', '),
      }
    })
  }

  paginationFunction = (data: StudentData[]) => {
    const { currentPage } = this
    const { perPage } = this
    if (isEmpty(currentPage) || isEmpty(perPage)) return data
    const upperIndex = currentPage * perPage
    const lowerIndex = (currentPage - 1) * perPage
    return data.slice(lowerIndex, upperIndex)
  }

  additionalOperations = [this.paginationFunction]

  changePage = (newPageNumber: number) => {
    this.currentPage = newPageNumber
  }

  changePerPage = (newPerPage: number) => {
    this.perPage = newPerPage
    this.currentPage = 1 // Reset to first page when perPage changes
  }

  <template>
    {{#let this.processor.transformedData this.processor.tableAction as |transformedData|}}
      <div data-test-time-on-task-table ...attributes>
        <table class="text-dusty-black-300 w-full">
          <thead class="{{this.headingClass}}">
            <tr>
              <th
                class="table-box table-box-header"
                {{on "click" (fn this.processor.tableActions.sortByValue "firstName")}}
                role="button"
                data-test-time-on-task-th
              >
                <div class="flex items-center justify-between">
                  <span>{{t "tables.timeOnTask.headers.firstName"}}</span>
                  <SortColumnIcon @sortingPriorityQueue={{this.sortingPriorityQueue}} @columnName="firstName" />
                </div>
              </th>
              <th
                class="table-box table-box-header"
                {{on "click" (fn this.processor.tableActions.sortByValue "lastName")}}
                role="button"
                data-test-time-on-task-th
              >
                <div class="flex items-center justify-between">
                  <span>{{t "tables.timeOnTask.headers.lastName"}}</span>
                  <SortColumnIcon @sortingPriorityQueue={{this.sortingPriorityQueue}} @columnName="lastName" />
                </div>
              </th>
              <th
                class="table-box table-box-header"
                {{on "click" (fn this.processor.tableActions.sortByValue "schoolClasses")}}
                role="button"
                data-test-time-on-task-th
              >
                <div class="flex items-center justify-between">
                  <span>{{t "tables.timeOnTask.headers.class"}}</span>
                  <SortColumnIcon @sortingPriorityQueue={{this.sortingPriorityQueue}} @columnName="schoolClasses" />
                </div>
              </th>
              {{#if @isSubco}}
                <th
                  class="table-box table-box-header"
                  {{on "click" (fn this.processor.tableActions.sortByValue "teacherName")}}
                  role="button"
                  data-test-time-on-task-th
                >
                  <div class="flex items-center justify-between">
                    <span>{{t "tables.timeOnTask.headers.teacherName"}}</span>
                    <SortColumnIcon @sortingPriorityQueue={{this.sortingPriorityQueue}} @columnName="teacherName" />
                  </div>
                </th>
                <th
                  class="table-box table-box-header"
                  {{on "click" (fn this.processor.tableActions.sortByNumber "gradePosition")}}
                  role="button"
                  data-test-time-on-task-th
                >
                  <div class="flex items-center justify-between">
                    <span>{{t "grade.label"}}</span>
                    <SortColumnIcon @sortingPriorityQueue={{this.sortingPriorityQueue}} @columnName="gradePosition" />
                  </div>
                </th>
              {{/if}}
              <th
                class="table-box table-box-header"
                {{on "click" (fn this.processor.tableActions.sortByNumber "total_seconds")}}
                role="button"
                data-test-time-on-task-th
              >
                <div class="flex items-center justify-between">
                  <span>{{t "tables.timeOnTask.headers.totalHours"}}</span>
                  <SortColumnIcon @sortingPriorityQueue={{this.sortingPriorityQueue}} @columnName="total_seconds" />
                </div>
              </th>
              <th
                class="table-box table-box-header"
                {{on "click" (fn this.processor.tableActions.sortByNumber "average_seconds_per_week")}}
                role="button"
                data-test-time-on-task-th
              >
                <div class="flex items-center justify-between">
                  <span>{{t "tables.timeOnTask.headers.avgHoursByWeek" htmlSafe=true}}</span>
                  <SortColumnIcon
                    @sortingPriorityQueue={{this.sortingPriorityQueue}}
                    @columnName="average_seconds_per_week"
                  />
                </div>
              </th>
              <th
                class="table-box table-box-header"
                {{on "click" (fn this.processor.tableActions.sortByNumber "session_count")}}
                role="button"
                data-test-time-on-task-th
              >
                <div class="flex items-center justify-between">
                  <span>{{t "tables.timeOnTask.headers.totalSessions" htmlSafe=true}}</span>
                  <SortColumnIcon @sortingPriorityQueue={{this.sortingPriorityQueue}} @columnName="session_count" />
                </div>
              </th>
              <th
                class="table-box table-box-header"
                {{on "click" (fn this.processor.tableActions.sortByNumber "average_seconds_per_session")}}
                role="button"
                data-test-time-on-task-th
              >
                <div class="flex items-center justify-between">
                  <span>{{t "tables.timeOnTask.headers.avgTimeInSession" htmlSafe=true}}</span>
                  <SortColumnIcon
                    @sortingPriorityQueue={{this.sortingPriorityQueue}}
                    @columnName="average_seconds_per_session"
                  />
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {{#each transformedData as |row index|}}
              <tr
                class="{{if
                    (eq @subscriptionType 'reading')
                    'even:bg-oceany-blue-50 hover:bg-oceany-blue-100 bg-white'
                  }}
                  {{if (eq @subscriptionType 'maths') 'even:bg-ms-green-50 hover:bg-ms-green-100 bg-white'}}"
                data-test-table-row={{index}}
              >
                <td class="table-box table-box-cell" data-test-first-name={{index}}>
                  {{formatValue row.firstName}}
                </td>
                <td class="table-box table-box-cell">
                  {{formatValue row.lastName}}
                </td>
                <td class="table-box table-box-cell">
                  {{formatValue row.schoolClasses}}
                </td>
                {{#if @isSubco}}
                  <td class="table-box table-box-cell" data-test-teacher-names>
                    {{formatValue row.teacherName}}
                  </td>
                  <td class="table-box table-box-cell">
                    {{gradeName row.gradePosition}}
                  </td>
                {{/if}}
                <td class="table-box table-box-cell">
                  {{if row.total_seconds (formatValue (formatDuration row.total_seconds))}}
                </td>
                <td class="table-box table-box-cell">
                  {{formatValue (formatDuration row.average_seconds_per_week)}}
                </td>
                <td data-test-session-count class="table-box table-box-cell">
                  {{formatValue row.session_count}}
                </td>
                <td class="table-box table-box-cell">
                  {{formatValue (formatDuration row.average_seconds_per_session)}}
                </td>
              </tr>
            {{/each}}
          </tbody>
        </table>
        {{! Table for export }}
        <div class="hidden">
          <table exportable>
            <thead>
              <tr>
                <th>{{t "tables.timeOnTask.headers.firstName"}}</th>
                <th>{{t "tables.timeOnTask.headers.lastName"}}</th>
                <th>{{t "reporting.dashboardTables.studentExternalId"}}</th>
                <th>{{t "tables.timeOnTask.headers.class"}}</th>
                {{#if @isSubco}}
                  <th data-test-teacher-name>{{t "tables.timeOnTask.headers.teacherName"}}</th>
                  <th>{{t "grade.label"}}</th>
                {{/if}}
                <th>{{t "tables.timeOnTask.headers.totalHours"}}</th>
                <th>{{t "tables.timeOnTask.headers.avgHoursByWeek" htmlSafe=true}}</th>
                <th>{{t "tables.timeOnTask.headers.totalSessions" htmlSafe=true}}</th>
                <th>{{t "tables.timeOnTask.headers.avgTimeInSession" htmlSafe=true}}</th>
              </tr>
            </thead>
            <tbody>
              {{#each this.data as |row|}}
                <tr>
                  <td>{{formatValue row.firstName}}</td>
                  <td>{{formatValue row.lastName}}</td>
                  <td>{{formatValue row.externalId}}</td>
                  <td>{{formatValue row.schoolClasses}}</td>
                  {{#if @isSubco}}
                    <td>{{formatValue row.teacherName}}</td>
                    <td>{{gradeName row.gradePosition}}</td>
                  {{/if}}
                  <td>{{formatValue (formatDuration row.total_seconds)}}</td>
                  <td>{{formatValue (formatDuration row.average_seconds_per_week)}}</td>
                  <td>{{formatValue row.session_count}}</td>
                  <td>{{formatValue (formatDuration row.average_seconds_per_session)}}</td>
                </tr>
              {{/each}}
            </tbody>
          </table>
        </div>
        <BasePageSelector
          class="mx-2 my-4"
          @pageCount={{this.pageCount}}
          @currentPage={{this.currentPage}}
          @changePage={{this.changePage}}
          @itemCount={{this.itemCount}}
          @itemsPerPage={{this.perPage}}
          @changePerPage={{this.changePerPage}}
        />
      </div>
    {{/let}}
  </template>
}

export default TimeOnTaskTable
